import React, { useState, useEffect, useRef } from "react";
import {
	fetchEtablissementAModifier,
	fetchSousCategories,
} from "./Recuperer_Etablissement_A_Modifier";
import { RecenterModal } from "./Fonctions_Cartographie";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { envoyerEmailOuVerification } from "./VerifierEmail";
import Modal_Remerciement from "./Modal_Remerciement";
import "../css/Ajout_Etablissements.css";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

//Pour mettre en forme les résultats à afficher dans la liste des résultats de recherche
function capitalizeFirstLetter(str) {
	if (!str) return ""; // Gérer les chaînes vides
	return str
		.toLowerCase()
		.replace(/(?:^|[ '-])\w/g, (match) => match.toUpperCase());
}

// Génération d'un identifiant qui sera utilisé en tant qu'identifiant de Finess pour l'établissement créé
function generer_random_finess_et() {
	const characters =
		"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let randomID = "";
	for (let i = 0; i < 9; i++) {
		const randomIndex = Math.floor(Math.random() * characters.length);
		randomID += characters.charAt(randomIndex);
	}
	return randomID;
}

// Fonction pour rechercher les coordonnées géographiques d'une adresse donnée
const rechercher_coordonnées = async (
	EtablissementChoisi,
	setEtablissementChoisi
) => {
	if (
		EtablissementChoisi.numero &&
		EtablissementChoisi.voirie &&
		EtablissementChoisi.ville &&
		EtablissementChoisi.code_postal
	) {
		const fullAddress =
			`${EtablissementChoisi.numero} ${EtablissementChoisi.voirie}, ${EtablissementChoisi.ville}, ${EtablissementChoisi.code_postal}`.trim();
		console.log(fullAddress);

		try {
			const geocodeUrl = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(
				fullAddress
			)}`;
			const response = await fetch(geocodeUrl);
			const data = await response.json();
			console.log(data);

			if (data.features && data.features.length > 0) {
				const { coordinates } = data.features[0].geometry;
				const [longitude, latitude] = coordinates;
				console.log("Coordonnées trouvées :", latitude, longitude);

				// Création d'un nouvel objet mis à jour
				let updatedEtablissement = {
					...EtablissementChoisi,
					latitude,
					longitude,
				};

				try {
					const proprietes = data.features[0].properties;
					const { city, postcode, street, housenumber, label } = proprietes;

					updatedEtablissement = {
						...updatedEtablissement,
						ville: city || EtablissementChoisi.ville,
						code_postal: postcode || EtablissementChoisi.code_postal,
						voirie: street || EtablissementChoisi.voirie,
						numero: housenumber || EtablissementChoisi.numero,
						adresse_finale: label || EtablissementChoisi.adresse_finale,
					};
				} catch (error) {
					console.error(
						"Erreur lors de la récupération des propriétés :",
						error
					);
				}

				// Mise à jour de l'état
				setEtablissementChoisi(updatedEtablissement);

				// Retourner la nouvelle version
				return updatedEtablissement;
			} else {
				console.error("Aucune adresse trouvée.");
				return null;
			}
		} catch (error) {
			console.error("Erreur lors de la récupération des coordonnées :", error);
			return null;
		}
	}
	return null;
};

// Fonction pour envoyer les modifications apportées à un établissement à la base de données
const mise_a_jour_etablissement_sur_la_bdd = async (
	EtablissementChoisi,
	setEtablissementChoisi,
	email,
	setShowThankYouModal
) => {
	try {
		let updatedEtablissement = { ...EtablissementChoisi };

		if (!updatedEtablissement.finess_et) {
			const finess_et = generer_random_finess_et();
			updatedEtablissement.finess_et = finess_et;

			setEtablissementChoisi((prevState) => ({
				...prevState,
				finess_et: finess_et,
			}));
		}

		// Attendre la mise à jour des coordonnées avant de continuer
		updatedEtablissement = await rechercher_coordonnées(
			updatedEtablissement,
			setEtablissementChoisi
		);

		if (
			!updatedEtablissement ||
			!updatedEtablissement.latitude ||
			!updatedEtablissement.longitude
		) {
			console.error(
				"Erreur : Impossible de récupérer les nouvelles coordonnées."
			);
			return;
		}

		const payload = {
			...updatedEtablissement,
			email,
		};

		const response = await fetch(
			`https://carto.api.toobib.org/api/upsert_finess_modifications?lat=${updatedEtablissement.latitude}&lon=${updatedEtablissement.longitude}&email=${email}`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(payload),
			}
		);

		if (!response.ok) {
			throw new Error(
				`Error updating finess_modifications: ${response.statusText}`
			);
		}

		const data = await response.json();
		console.log("Data successfully updated:", data);

		// Affichage d'un modal qui remercie l'utilisateur pour sa contribution
		setShowThankYouModal(true);
	} catch (err) {
		console.error("Error pushing modifications:", err);
	}
};

const AjoutEtablissement = () => {
	// Initialisation des états
	const [numeroFinessRecherche, setNumeroFinessRecherche] = useState(""); // Numéro de finess de l'établissement recherché par l'utilisateur
	const [nomEtablissementRecherche, setNomEtablissementRecherche] =
		useState(""); // Nom de l'établissement recherché par l'utilisateur
	const [
		donneesCompletesEtablissementRecherche,
		setDonneesCompletesEtablissementRecherche,
	] = useState([]); // Données complètes de l'établissement recherché par l'utilisateur, récupérées depuis la bdd
	const [emailsVerifies, setEmailVerifies] = useState([]); // Liste des emails vérifiés qui sont enregistrés dans la base de donnée
	const [emailUtilisateur, setEmailUtilisateur] = useState(""); // Email de l'utilisateur
	const [
		modificationParUtilisateurPermise,
		setModificationParUtilisateurPermise,
	] = useState(false); // Indique si l'utilisateur a le droit de modifier les informations des établissements
	const [etablissementsSontTrouves, setEtablissementsSontTrouves] =
		useState(false); // Indique si des établissements correspondant à la recherche de l'utilisateur ont été trouvés dans la bdd

	// On vérifie que l'email de l'utilisateur soit enregistré dans la base de données pour lui permettre de modifier les établissements
	useEffect(() => {
		emailsVerifies.includes(emailUtilisateur)
			? setModificationParUtilisateurPermise(true)
			: setModificationParUtilisateurPermise(false);
	}, [emailsVerifies, emailUtilisateur]);

	// On vérifie régulièrement si l'email de l'utilisateur est enregistré dans la base de données
	useEffect(() => {
		let intervalle_de_recuperation_emails_verifies;
		const fetchEmails = async () => {
			try {
				const response = await fetch(
					"https://carto.api.toobib.org/api/get_verified_emails"
				);
				if (!response.ok) {
					throw new Error("Failed to fetch verified emails");
				}
				const data = await response.json();
				setEmailVerifies(data);
			} catch (error) {
				console.error("Error fetching verified emails:", error);
			}
		};
		intervalle_de_recuperation_emails_verifies = setInterval(fetchEmails, 2000);
		fetchEmails();
		return () => {
			if (intervalle_de_recuperation_emails_verifies) {
				clearInterval(intervalle_de_recuperation_emails_verifies);
			}
		};
	}, []);

	// Fonction pour rechercher un établissement dans la base de données, à partir des caractéristiques renseignées par l'utilisateur (nom, numéro de finess, catégorie)
	const rechercher_etablissement_incomplet_dans_bdd = async () => {
		try {
			await fetchEtablissementAModifier(
				numeroFinessRecherche,
				nomEtablissementRecherche,
				setDonneesCompletesEtablissementRecherche,
				sousCategorieChoisieEtablissement
			);
			setEtablissementsSontTrouves(true);
		} catch (error) {
			console.error("Error fetching data:", error);
		}
	};

	const [informationsEtablissement, setInformationsEtablissement] =
		useState(null); // Informations de l'établissement créé par l'utilisateur
	const [etablissementEstSelectionne, setEtablissementEstSelectionne] =
		useState(false); // Indique si un établissement a été sélectionné par l'utilisateur parmi les résultats de recherche d'établissements
	const [
		fenetreModificationEtablissementAffichee,
		setFenetreModificationEtablissementAffichee,
	] = useState(false); // Indique si la fenêtre de modification de l'établissement est affichée
	const [isAnimating, setIsAnimating] = useState(false);

	// Fonction pour sélectionner un établissement parmi les résultats de recherche
	// (gère aussi l'affichage du scrollview des établissements correspondant aux critères de recherche de l'utilisateur)
	const handleSelect = (etablissement) => {
		setUtilisateurVeutCreer(false);
		setInformationsEtablissement(etablissement);
		setEtablissementEstSelectionne(true);
		setFenetreModificationEtablissementAffichee(true);
		const etablissementSelectionne = document.querySelector(
			`#etablissement-${etablissement.finess_et}`
		);
		if (etablissementSelectionne) {
			etablissementSelectionne.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
		setTimeout(() => setIsAnimating(true), 10);
	};

	const [UtilisateurVeutCreer, setUtilisateurVeutCreer] = useState(false);

	// Fonction pour afficher le panneau de création d'un nouvel établissement
	const creationEtablissement = () => {
		setDonneesCompletesEtablissementRecherche([]);
		setEtablissementsSontTrouves(false);
		setUtilisateurVeutCreer(true);
		setInformationsEtablissement({
			finess_et: "",
			code_postal: "",
			latitude: "",
			longitude: "",
			numero: "",
			voirie: "",
			adresse_finale: "",
			sous_categorie: "",
			rs: "",
			telephone: "",
		});
		wait(4000);
		setEtablissementEstSelectionne(true);
		setFenetreModificationEtablissementAffichee(true);
		setTimeout(() => setIsAnimating(true), 10);
	};

	const [sousCategoriesFiness, setSousCategoriesFiness] = useState([]); // Récupération des sous-catégories d'établissements existantes dans le finess
	useEffect(() => {
		fetchSousCategories(setSousCategoriesFiness);
	}, []);

	const [inputSousCategorie, setInputSousCategorie] = useState(""); // Input entré par l'utilisateur pour rechercher une sous catégorie
	const [sousCategoriesFiltrees, setSousCategoriesFiltrees] =
		useState(sousCategoriesFiness); // Liste des sous-catégories filtrées en fonction de la recherche de l'utilisateur
	const [
		sousCategorieChoisieEtablissement,
		setSousCategorieChoisieEtablissement,
	] = useState(""); // Sous-catégorie recherchée par l'utilisateur
	const [afficherDropdownSousCategories, setAfficherDropdownSousCategories] =
		useState(false); // Indique si le dropdown des sous-catégories doit être affiché

	const dropdownRef = useRef(null); // Référence pour le dropdown des sous-catégories

	// Fonction pour filtrer les sous-catégories en fonction de la recherche de l'utilisateur
	const handleRechercheSousCategorie = (e) => {
		const query = e.target.value.toLowerCase();
		setInputSousCategorie(query);
		setSousCategoriesFiltrees(
			sousCategoriesFiness.filter((category) =>
				category.toLowerCase().includes(query)
			)
		);
		setAfficherDropdownSousCategories(true);
	};

	// Fonction pour enregistrer la sous-catégorie sélectionnée par l'utilisateur parmi les résultats de recherche
	const handleSelectCategory = (category) => {
		setSousCategorieChoisieEtablissement(category);
		setAfficherDropdownSousCategories(false);
		setInputSousCategorie(category);
	};

	// Fonction pour fermer le dropdown des sous-catégories si l'utilisateur clique en dehors de celui-ci
	const handleClickOutsideSousCategories = (e) => {
		if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
			setAfficherDropdownSousCategories(false);
		}
	};

	// Gestion de l'événement de clic en dehors du dropdown des sous-catégories
	useEffect(() => {
		document.addEventListener("click", handleClickOutsideSousCategories);
		return () => {
			document.removeEventListener("click", handleClickOutsideSousCategories);
		};
	}, []);

	const [showThankYouModal, setShowThankYouModal] = useState(false); // Indique si le modal de remerciement pour la contribution de l'utilisateur doit être affiché

	return (
		<div
			className="AE-conteneur-global"
			style={{ display: "flex", width: "100vw", height: "100vh" }}>
			<div className="AE-conteneur-partie-gauche-ecran">
				<Modal_Remerciement
					show={showThankYouModal}
					onClose={() => {
						setShowThankYouModal(false);
						window.close();
					}}
				/>

				{fenetreModificationEtablissementAffichee && (
					<div
						className={`AE-panneau-modification-etablissement ${
							isAnimating ? "show" : ""
						}`}>
						{UtilisateurVeutCreer ? (
							<div
								style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
								<input
									type="text"
									placeholder="Nom de l'établissement"
									className="AE-modification-input"
									value={informationsEtablissement.rs || ""}
									style={{ maxWidth: "60%" }}
									onChange={(event) => {
										setInformationsEtablissement((prevState) => ({
											...prevState,
											rs: event.target.value,
										}));
									}}
								/>
								<input
									type="text"
									placeholder="Numéro de FINESS"
									style={{ maxWidth: "30%" }}
									title="Ce numéro est obligatoire, vous pouvez le trouver sur internet"
									className="AE-modification-input nombre"
									value={informationsEtablissement.finess_et || ""}
									maxLength={9}
									onChange={(e) => {
										const regex = /^[a-zA-Z0-9]*$/;
										if (regex.test(e.target.value)) {
											setInformationsEtablissement((prevState) => ({
												...prevState,
												finess_et: e.target.value,
											}));
										}
									}}
								/>
							</div>
						) : (
							<div>{informationsEtablissement.rs}</div>
						)}
						<div className="modif-input-container">
							<input
								type="text"
								placeholder="N°"
								className="AE-modification-input numero-voie"
								value={informationsEtablissement.numero || ""}
								onChange={(event) =>
									setInformationsEtablissement((prevState) => ({
										...prevState,
										numero: event.target.value,
										adresse_finale: `${event.target.value} ${informationsEtablissement.voirie} ${informationsEtablissement.code_postal} ${informationsEtablissement.ville}`,
									}))
								}
							/>
							<input
								type="text"
								placeholder="Voirie"
								className="AE-modification-input voirie"
								value={informationsEtablissement.voirie || ""}
								onChange={(event) =>
									setInformationsEtablissement((prevState) => ({
										...prevState,
										voirie: event.target.value,
										adresse_finale: `${informationsEtablissement.numero} ${event.target.value} ${informationsEtablissement.code_postal} ${informationsEtablissement.ville}`,
									}))
								}
							/>
							<input
								type="text"
								placeholder="Ville"
								className="AE-modification-input ville"
								value={informationsEtablissement.ville || ""}
								onChange={(event) =>
									setInformationsEtablissement((prevState) => ({
										...prevState,
										ville: event.target.value,
										adresse_finale: `${informationsEtablissement.numero} ${informationsEtablissement.voirie} ${informationsEtablissement.code_postal} ${event.target.value}`,
									}))
								}
							/>
							<input
								type="text"
								placeholder="CP"
								className="AE-modification-input nombre"
								value={informationsEtablissement.code_postal || ""}
								onChange={(event) =>
									setInformationsEtablissement((prevState) => ({
										...prevState,
										code_postal: event.target.value,
										adresse_finale: `${informationsEtablissement.numero} ${informationsEtablissement.voirie} ${event.target.value} ${informationsEtablissement.ville}`,
									}))
								}
							/>
						</div>
						<div className="modif-input-container">
							<input
								type="text"
								placeholder="Téléphone"
								className="AE-modification-input telephone"
								value={informationsEtablissement.telephone || ""}
								onChange={(event) =>
									setInformationsEtablissement((prevState) => ({
										...prevState,
										telephone: event.target.value,
									}))
								}
							/>
							<select
								className="AE-modification-input categorie"
								value={informationsEtablissement.sous_categorie || ""}
								onChange={(event) =>
									setInformationsEtablissement((prevState) => ({
										...prevState,
										sous_categorie: event.target.value,
									}))
								}>
								<option value="" disabled>
									Choisissez une catégorie
								</option>
								{sousCategoriesFiness.map((category, index) => (
									<option key={index} value={category}>
										{category}
									</option>
								))}
							</select>
						</div>
						<div
							className="AE-conteneur-carte-lieu-etablissement"
							style={{ height: UtilisateurVeutCreer ? "60%" : "70%" }}>
							<MapContainer
								center={[
									informationsEtablissement.latitude,
									informationsEtablissement.longitude,
								]}
								zoom={15}
								style={{ width: "100%", height: "100%" }}>
								<RecenterModal
									coordinates={{
										lat: informationsEtablissement.latitude,
										lng: informationsEtablissement.longitude,
									}}
								/>
								<TileLayer
									url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
									attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
								/>
								<button
									onClick={() =>
										rechercher_coordonnées(
											informationsEtablissement,
											setInformationsEtablissement
										)
									}
									className="AE-bouton-actualisation-localisation"
									style={{
										width: "30px",
										backgroundColor: "#40375a",
									}}>
									<img
										src="lieu.png"
										alt="Reload"
										className="AE-bouton-actualisation-localisation-image"
									/>
								</button>
								<Marker
									position={[
										informationsEtablissement.latitude,
										informationsEtablissement.longitude,
									]}
									icon={L.icon({
										iconUrl: "epingle.png",
										iconSize: [35, 35],
										iconAnchor: [17.5, 35],
										popupAnchor: [0, -35],
										shadowUrl: "epingle_shadow.png",
										shadowSize: [41, 41],
									})}>
									<Popup>You are here</Popup>
								</Marker>
							</MapContainer>
						</div>
						<div
							className="AE-conteneur-bouton-valider-modifications"
							style={{ marginTop: "5%" }}>
							<button
								className="AE-bouton-valider-modifications"
								style={{
									backgroundColor: modificationParUtilisateurPermise
										? "#40375a"
										: "#d3d3d3",
									color: modificationParUtilisateurPermise
										? "#ffffff"
										: "#a9a9a9",
									cursor: modificationParUtilisateurPermise
										? "pointer"
										: "not-allowed",
									opacity: modificationParUtilisateurPermise ? 1 : 0.6,
								}}
								disabled={!modificationParUtilisateurPermise}
								onClick={() =>
									mise_a_jour_etablissement_sur_la_bdd(
										informationsEtablissement,
										setInformationsEtablissement,
										emailUtilisateur,
										setShowThankYouModal
									)
								}>
								Valider les modifications
							</button>
						</div>
					</div>
				)}
				<div
					className="AE-conteneur-validation-mail"
					style={{ bottom: etablissementEstSelectionne ? "0" : "-100px" }}>
					<input
						type="email"
						placeholder="Adresse email"
						className="AE-modification-input"
						value={emailUtilisateur}
						onChange={(e) => setEmailUtilisateur(e.target.value)}
						style={{ width: "75%", marginTop: "10%" }}
					/>
					<button
						type="button"
						disabled={modificationParUtilisateurPermise}
						className="AE-bouton-validation-mail"
						onClick={() =>
							envoyerEmailOuVerification(emailUtilisateur, null, "verification")
						}
						style={{ backgroundColor: "transparent", marginTop: "0px" }}>
						{!modificationParUtilisateurPermise
							? "Vérifier mon email"
							: "Votre email est déjà vérifié"}
					</button>
				</div>
			</div>
			<div className="AE-conteneur-panneau-droite">
				<button
					style={{ marginTop: "10%" }}
					className="AE-bouton-creation-etablissement"
					onClick={creationEtablissement}>
					Créer un nouvel établissement
				</button>
				<hr />
				<div className="AE-titre-recherche-etablissement-incomplet">
					<h style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>
						Rechercher un établissement incomplet
					</h>
				</div>
				<div
					className="AE-conteneur-dropdown-sous-categories"
					ref={dropdownRef}>
					<input
						type="text"
						placeholder="Catégorie de l'établissement"
						value={inputSousCategorie}
						onChange={handleRechercheSousCategorie}
						onFocus={() => setAfficherDropdownSousCategories(true)}
						className="AE-input-dropdown-sous-categories"
					/>
					{afficherDropdownSousCategories && (
						<ul className="AE-liste-dropdown-sous-categories">
							{sousCategoriesFiltrees.map((category) => (
								<li
									key={category}
									onClick={() => handleSelectCategory(category)}
									className="AE-item-dropdown-sous-categories">
									{category}
								</li>
							))}
						</ul>
					)}
				</div>
				<div className="barre-de-recherche-container">
					<div className="barre-de-recherche-finess">
						<input
							type="text"
							placeholder="N° FINESS"
							maxLength="9"
							className="recherche-finess"
							onChange={(e) => setNumeroFinessRecherche(e.target.value)}
						/>
					</div>
					<div className="barre-de-recherche-etablissement">
						<input
							type="text"
							placeholder="Nom de l'établissement"
							className="recherche-etablissement"
							onChange={(e) => setNomEtablissementRecherche(e.target.value)}
						/>
						<button
							disabled={
								(sousCategorieChoisieEtablissement.length === 0 ||
									nomEtablissementRecherche.length === 0) &&
								numeroFinessRecherche.length === 0
							}
							className="lancer-la-recherche-bouton"
							onClick={() => rechercher_etablissement_incomplet_dans_bdd()}>
							<img src="loupe.png" alt="Rechercher" className="bouton-image" />
						</button>
					</div>
				</div>

				{donneesCompletesEtablissementRecherche.length > 3 && (
					<div
						className="scroll-arrow top"
						onClick={() => {
							const container = document.querySelector(".liste-de-resultats");
							container.scrollBy({ top: -150, behavior: "smooth" });
						}}>
						<img
							src="/fleche_haut.png"
							alt="Scroll down"
							style={{ width: "20px", height: "20px" }}
						/>
					</div>
				)}

				<div
					className="liste-de-resultats"
					onScroll={(e) => {
						const container = e.target;
						const children = container.querySelectorAll(".resultat");
						const containerHeight = container.clientHeight;
						const scrollTop = container.scrollTop;

						children.forEach((child) => {
							const childTop = child.offsetTop - scrollTop;
							const distanceFromCenter = Math.abs(
								childTop - containerHeight / 2 + child.clientHeight / 2
							);
							const fadeLevel = Math.min(1, distanceFromCenter / 200);

							child.style.opacity = 1 - fadeLevel;
							child.style.transform = `scale(${1 - fadeLevel * 0.2})`;
						});
					}}>
					{donneesCompletesEtablissementRecherche.length > 0 ? (
						<ul style={{ listStyleType: "none", padding: 0 }}>
							{donneesCompletesEtablissementRecherche.map((etablissement) => (
								<li
									key={etablissement.finess_et}
									id={`etablissement-${etablissement.finess_et}`}
									className="resultat">
									<button onClick={() => handleSelect(etablissement)}>
										<strong>
											Nom: {capitalizeFirstLetter(etablissement.rs)}
										</strong>
										<br />
										<strong>N° de finess: {etablissement.finess_et}</strong>
										<br />
										<strong>
											Ville: {capitalizeFirstLetter(etablissement.ville)}
										</strong>
									</button>
								</li>
							))}
						</ul>
					) : etablissementsSontTrouves ? (
						<p
							style={{
								color: "white",
								fontSize: 15,
								marginTop: "30%",
								fontWeight: "bold",
							}}>
							Aucun établissement trouvé
						</p>
					) : (
						<></>
					)}
				</div>
				{donneesCompletesEtablissementRecherche.length > 3 && (
					<div
						className="scroll-arrow down"
						onClick={() => {
							const container = document.querySelector(".liste-de-resultats");
							container.scrollBy({ top: 150, behavior: "smooth" });
						}}>
						<img
							src="/fleche_bas.png"
							alt="Scroll down"
							style={{ width: "20px", height: "20px" }}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default AjoutEtablissement;
